/**
 * Dataset da expedição (Task 14, brief 3.7).
 *
 * Uma fonte (`/api/platform/fulfillment`) com o seu estado declarado, no mesmo
 * vocabulário das demais telas (`live` vs `awaiting_contract`): fonte ausente é
 * dito, nunca convertido em "nenhuma expedição".
 *
 * Os seletores aqui são a fonte ÚNICA de "quais tarefas compõem este recorte":
 * a fila, o detalhe e o rastreio leem daqui, então não existe contagem de tela
 * discordando de outra. Puro e sem I/O.
 */
import {
  FULFILLMENT_STAGES,
  OCCURRENCE_KINDS,
  OCCURRENCE_KIND_LABELS,
  FULFILLMENT_STAGE_LABELS,
  mergeFulfillmentView,
  nextStages,
  fulfillmentRequirementsFor,
  type FulfillmentStage,
  type FulfillmentUnifiedView,
  type OccurrenceKind,
  type FulfillmentStageField
} from "./state-machine.js";
import type { DashboardSourceState } from "../dashboard/kpis.js";
import type { FulfillmentEvent, FulfillmentTask } from "../api/types.js";

export interface FulfillmentSources {
  fulfillmentTasks: DashboardSourceState;
}

export interface FulfillmentDataset {
  tasks: FulfillmentTask[];
  sources: FulfillmentSources;
}

/** Estado inicial honesto: nada buscado ainda (não é "nenhuma expedição"). */
export function emptyFulfillmentDataset(): FulfillmentDataset {
  return { tasks: [], sources: { fulfillmentTasks: "awaiting_contract" } };
}

export function isFulfillmentSourceAwaitingContract(dataset: FulfillmentDataset): boolean {
  return dataset.sources.fulfillmentTasks === "awaiting_contract";
}

export function fulfillmentTaskById(dataset: FulfillmentDataset, taskId: string): FulfillmentTask | null {
  return dataset.tasks.find((task) => task.id === taskId) ?? null;
}

export function fulfillmentTaskByOrderId(dataset: FulfillmentDataset, platformOrderId: string): FulfillmentTask | null {
  return dataset.tasks.find((task) => task.platformOrderId === platformOrderId) ?? null;
}

export function tasksByStage(dataset: FulfillmentDataset, stage: FulfillmentStage): FulfillmentTask[] {
  return dataset.tasks.filter((task) => task.stage === stage);
}

/** Tarefas com ocorrência pendente — a fila de exceções que precisa de ação. */
export function tasksWithOccurrence(dataset: FulfillmentDataset): FulfillmentTask[] {
  return dataset.tasks.filter((task) => task.occurrenceKind !== null);
}

export interface StageBreakdownRow {
  stage: FulfillmentStage;
  label: string;
  count: number;
}

/** Composição da fila por etapa, em ordem estável. */
export function stageBreakdown(dataset: FulfillmentDataset): StageBreakdownRow[] {
  return FULFILLMENT_STAGES.map((stage) => ({
    stage,
    label: FULFILLMENT_STAGE_LABELS[stage],
    count: tasksByStage(dataset, stage).length
  }));
}

export interface OccurrenceBreakdownRow {
  kind: OccurrenceKind;
  label: string;
  count: number;
}

/** Composição das ocorrências pendentes por tipo. */
export function occurrenceBreakdown(dataset: FulfillmentDataset): OccurrenceBreakdownRow[] {
  return OCCURRENCE_KINDS.map((kind) => ({
    kind,
    label: OCCURRENCE_KIND_LABELS[kind],
    count: dataset.tasks.filter((task) => task.occurrenceKind === kind).length
  }));
}

/**
 * Linha do tempo da tarefa: do passo mais antigo ao mais recente.
 *
 * Ordenação estável por `occurredAt` e, em empate, pela ordem em que o servidor
 * publicou — dois passos no mesmo milissegundo (criação + espelho do rastreio)
 * não podem trocar de lugar entre renderizações.
 */
export function fulfillmentTimeline(task: FulfillmentTask): FulfillmentEvent[] {
  return task.events
    .map((event, index) => ({ event, index }))
    .sort((a, b) => a.event.occurredAt.localeCompare(b.event.occurredAt) || a.index - b.index)
    .map((entry) => entry.event);
}

export function lastFulfillmentEvent(task: FulfillmentTask): FulfillmentEvent | null {
  return fulfillmentTimeline(task).at(-1) ?? null;
}

/**
 * Visão unificada (D-14.4): estado interno + estado do transportador.
 * Delega a classificação à regra pura — a tela não decide a relação entre os
 * dois lados.
 */
export function unifiedView(task: FulfillmentTask): FulfillmentUnifiedView {
  return mergeFulfillmentView(task.stage, task.tracking.status);
}

export interface AvailableStageTransition {
  to: FulfillmentStage;
  requires: readonly FulfillmentStageField[];
}

/**
 * Próximas etapas que a tela pode oferecer, com o que cada uma exige. Vem da
 * máquina pura — a tela não mantém a sua própria lista de botões.
 */
export function availableStageTransitions(task: FulfillmentTask): AvailableStageTransition[] {
  return nextStages(task.stage).map((to) => ({ to, requires: fulfillmentRequirementsFor(to) }));
}

export interface OrderDrillDown {
  route: string;
  label: string;
  reference: string;
}

/**
 * Para onde navegar para ver o PEDIDO que esta tarefa expede (D-03 aplicado ao
 * brief 3.7). Leva ao detalhe do pedido unificado — mesmo registro, mesma
 * origem; a expedição não é uma segunda verdade sobre a venda.
 */
export function orderDrillDown(task: FulfillmentTask): OrderDrillDown {
  return {
    route: `/pedidos/${encodeURIComponent(task.platformOrderId)}`,
    label: "Abrir o pedido que esta expedição despacha",
    reference: task.orderExternalId
  };
}
